// ============================================================================
//  DATOS DEL RECORRIDO DE PLAYBOOK (home)
// ----------------------------------------------------------------------------
//  Capa de datos DESACOPLADA de la vista: hoy es un mock local, mañana puede
//  ser la respuesta de un endpoint sin tocar los componentes. Por eso:
//
//   - La forma imita lo que devolvería una API (playbook > stages > processes),
//     no la estructura del JSX.
//   - Los textos van como { es, en } y se resuelven con pickLang(), el mismo
//     patrón que ya usa src/data/termsContent.js. Se evita así meter ~120
//     claves de copy en i18n.js, y es además como los serviría el backend
//     (contenido localizado, no claves de traducción).
//   - Las cifras llevan SIEMPRE `isEstimate: true`. Son ejemplos ilustrativos,
//     NO resultados medidos, y la interfaz está obligada a decirlo.
//
//  OJO con lo que se publica aquí: la decisión de producto vigente prohíbe
//  presentar como vendible (y con PVP) lo que todavía no está construido. Por
//  eso los procesos de roadmap llevan `status: 'roadmap'` y NO llevan precio.
// ============================================================================

export const pickLang = (value, lang) => {
   if (!value) return '';
   if (typeof value === 'string') return value;
   return value[lang] || value.es || value.en || '';
};

// Estados posibles de un proceso. 'live' = en producción y vendible hoy.
export const PROCESS_STATUS = {
   LIVE: 'live',
   ROADMAP: 'roadmap',
};

// Conceptos de la rueda del mensaje principal. `suffix` va por concepto a
// propósito: en castellano "ya resuelto" tiene que concordar en género y
// numero, y con un sufijo fijo saldría "las credenciales ya resuelto".
export const valueMessageItems = [
   { key: 'onboarding', concept: { es: 'el onboarding', en: 'onboarding' }, suffix: { es: 'ya resuelto', en: 'already solved' } },
   { key: 'credentials', concept: { es: 'las credenciales seguras', en: 'secure credentials' }, suffix: { es: 'ya resueltas', en: 'already solved' } },
   { key: 'data', concept: { es: 'la protección de datos', en: 'data protection' }, suffix: { es: 'ya resuelta', en: 'already solved' } },
   { key: 'setup', concept: { es: 'la configuración técnica', en: 'technical setup' }, suffix: { es: 'ya resuelta', en: 'already solved' } },
   { key: 'commercial', concept: { es: 'los procesos comerciales', en: 'sales processes' }, suffix: { es: 'ya resueltos', en: 'already solved' } },
   { key: 'pricing', concept: { es: 'los precios de venta sugeridos', en: 'suggested selling prices' }, suffix: { es: 'ya resueltos', en: 'already solved' } },
   { key: 'roi', concept: { es: 'la medición del ROI', en: 'ROI measurement' }, suffix: { es: 'ya resuelta', en: 'already solved' } },
   { key: 'reporting', concept: { es: 'el reporting para el cliente', en: 'client reporting' }, suffix: { es: 'ya resuelto', en: 'already solved' } },
   { key: 'maintenance', concept: { es: 'el mantenimiento y las actualizaciones', en: 'maintenance and updates' }, suffix: { es: 'ya resueltos', en: 'already solved' } },
];

export const playbookStory = {
   id: 'reception-playbook',
   name: {
      es: 'Playbook de Recepción y Citas',
      en: 'Reception & Appointments Playbook',
   },
   description: {
      es: 'Un sistema completo para que una agencia venda atención al cliente y gestión de citas sin construir ni mantener nada.',
      en: 'A complete system so an agency can sell customer reception and appointment management without building or maintaining anything.',
   },
   // Aviso obligatorio en cualquier sitio donde se pinten cifras.
   estimateNotice: {
      es: 'Cifras de ejemplo con fines ilustrativos.',
      en: 'Example figures for illustration only.',
   },
   stages: [
      // ---------------------------------------------------------------- 1
      {
         key: 'choose',
         icon: 'grid',
         name: { es: 'Elige qué vender', en: 'Choose what to sell' },
         headline: {
            es: 'Empieza con un sistema completo o construye una oferta a medida con procesos individuales.',
            en: 'Start with a complete system or build a tailored offer from individual processes.',
         },
         // Esta etapa no pinta procesos: pinta las dos vías de entrada.
         options: [
            {
               key: 'playbook',
               primary: true,
               title: { es: 'Playbook completo', en: 'Complete Playbook' },
               text: {
                  es: 'Un sistema ya ensamblado que resuelve una necesidad de principio a fin.',
                  en: 'An already-assembled system that solves a need end to end.',
               },
               to: '/playbooks',
            },
            {
               key: 'catalog',
               primary: false,
               title: { es: 'Procesos del Catálogo', en: 'Catalogue processes' },
               text: {
                  es: 'Elige agentes y procesos sueltos para personalizar o ampliar una solución.',
                  en: 'Pick individual agents and processes to tailor or extend a solution.',
               },
               to: '/services',
            },
         ],
      },
      // ---------------------------------------------------------------- 2
      {
         key: 'onboarding',
         icon: 'clipboard',
         name: { es: 'Incorpora al cliente', en: 'Onboard the client' },
         headline: {
            es: 'Un onboarding inteligente recoge todo lo que el servicio necesita para personalizarse.',
            en: 'A smart onboarding collects everything the service needs to tailor itself.',
         },
         // La demo real se abre en modal; el iframe NO se carga hasta abrirlo.
         demo: {
            key: 'discovery',
            label: { es: 'Probar onboarding', en: 'Try the onboarding' },
            title: {
               es: 'Onboarding inteligente · demo',
               en: 'Smart onboarding · demo',
            },
         },
         collects: [
            { es: 'Información del negocio', en: 'Business information' },
            { es: 'Contexto y objetivos', en: 'Context and goals' },
            { es: 'Procesos actuales', en: 'Current processes' },
            { es: 'Datos para personalizar el servicio', en: 'Data to tailor the service' },
         ],
      },
      // ---------------------------------------------------------------- 3
      {
         key: 'connect',
         icon: 'shield',
         name: { es: 'Conecta sin pedir contraseñas', en: 'Connect without asking for passwords' },
         headline: {
            es: 'Tu cliente conecta sus herramientas de forma segura. Tú no gestionas contraseñas.',
            en: 'Your client connects their tools securely. You never handle passwords.',
         },
         demo: {
            key: 'sca',
            label: { es: 'Ver el portal seguro', en: 'See the secure portal' },
            title: { es: 'Conexión segura · demo', en: 'Secure connection · demo' },
         },
         steps: [
            {
               es: 'La agencia solicita las credenciales que hacen falta.',
               en: 'The agency requests the credentials it needs.',
            },
            {
               es: 'El cliente las introduce en un entorno seguro.',
               en: 'The client enters them in a secure environment.',
            },
            {
               es: 'Nadie manda contraseñas por email ni WhatsApp.',
               en: 'Nobody sends passwords over email or WhatsApp.',
            },
            {
               es: 'Las conexiones quedan asociadas a su proyecto.',
               en: 'Connections stay bound to their project.',
            },
         ],
      },
      // ---------------------------------------------------------------- 4
      {
         key: 'activate',
         icon: 'zap',
         name: { es: 'Activa el servicio', en: 'Activate the service' },
         headline: {
            es: 'Procesos ya construidos, conectados y mantenidos. No hay nada que programar.',
            en: 'Processes already built, connected and maintained. Nothing to code.',
         },
         processes: [
            {
               key: 'whatsapp-reception',
               status: PROCESS_STATUS.LIVE,
               engine: { es: 'Motor de Atención', en: 'Service engine' },
               title: { es: 'Recepcionista por WhatsApp', en: 'WhatsApp receptionist' },
               problem: {
                  es: 'Se pierden mensajes fuera de horario y nadie contesta a tiempo.',
                  en: 'Messages are missed after hours and nobody replies in time.',
               },
               result: {
                  es: 'Atiende, resuelve dudas y deriva lo que necesita a una persona.',
                  en: 'Answers, resolves questions and escalates what needs a human.',
               },
               automates: { es: 'Atención al cliente', en: 'Customer service' },
               suggestedPrice: { amount: 250, currency: 'EUR', period: 'month' },
               impact: {
                  es: 'Cobertura 24/7 sin ampliar el equipo',
                  en: '24/7 coverage without growing the team',
               },
               isEstimate: true,
            },
            {
               key: 'appointments',
               status: PROCESS_STATUS.LIVE,
               engine: { es: 'Motor de Atención', en: 'Service engine' },
               title: { es: 'Gestión y reserva de citas', en: 'Appointment booking' },
               problem: {
                  es: 'La agenda se llena a mano y con llamadas cruzadas.',
                  en: 'The calendar is filled by hand and crossed phone calls.',
               },
               result: {
                  es: 'La cita se reserva sola y queda en la agenda del cliente.',
                  en: 'The appointment books itself straight into the client calendar.',
               },
               automates: { es: 'Agenda', en: 'Scheduling' },
               impact: {
                  es: 'Menos huecos vacíos en la agenda',
                  en: 'Fewer empty slots in the calendar',
               },
               isEstimate: true,
            },
            {
               key: 'reminders',
               status: PROCESS_STATUS.LIVE,
               engine: { es: 'Motor de Atención', en: 'Service engine' },
               title: { es: 'Recordatorios de cita', en: 'Appointment reminders' },
               problem: {
                  es: 'Las ausencias sin avisar cuestan huecos que ya no se recuperan.',
                  en: 'No-shows cost slots that can no longer be recovered.',
               },
               result: {
                  es: 'Avisa antes de la cita y recoge confirmaciones o cambios.',
                  en: 'Reminds before the appointment and collects confirmations or changes.',
               },
               automates: { es: 'Confirmaciones', en: 'Confirmations' },
               impact: {
                  es: 'Menos ausencias sin avisar',
                  en: 'Fewer unannounced no-shows',
               },
               isEstimate: true,
            },
            {
               key: 'commercial-followup',
               status: PROCESS_STATUS.LIVE,
               engine: { es: 'Motor de Ventas', en: 'Sales engine' },
               title: { es: 'Seguimiento comercial', en: 'Sales follow-up' },
               problem: {
                  es: 'Quien pregunta y no reserva se queda sin seguimiento.',
                  en: 'People who ask and do not book get no follow-up.',
               },
               result: {
                  es: 'Retoma la conversación y recupera la oportunidad.',
                  en: 'Picks the conversation back up and recovers the opportunity.',
               },
               automates: { es: 'Seguimiento 1:1', en: '1:1 follow-up' },
               suggestedPrice: { amount: 75, currency: 'EUR', period: 'month' },
               impact: {
                  es: 'Oportunidades que no se enfrían',
                  en: 'Opportunities that do not go cold',
               },
               isEstimate: true,
            },
            {
               key: 'voice-reception',
               // ROADMAP: se representa, pero NO como vendible y SIN precio.
               status: PROCESS_STATUS.ROADMAP,
               engine: { es: 'Motor de Atención', en: 'Service engine' },
               title: { es: 'Recepcionista de voz', en: 'Voice receptionist' },
               problem: {
                  es: 'Las llamadas siguen exigiendo a alguien al teléfono.',
                  en: 'Phone calls still need somebody on the line.',
               },
               result: {
                  es: 'Atiende la llamada y la resuelve o la transfiere.',
                  en: 'Takes the call and either resolves or transfers it.',
               },
               automates: { es: 'Llamadas', en: 'Phone calls' },
            },
         ],
      },
      // ---------------------------------------------------------------- 5
      {
         key: 'expand',
         icon: 'layers',
         name: { es: 'Amplía el servicio', en: 'Expand the service' },
         headline: {
            es: 'Empieza resolviendo una necesidad y amplía el sistema conforme crece el cliente.',
            en: 'Start by solving one need and grow the system as the client grows.',
         },
         groups: [
            {
               key: 'pre',
               title: { es: 'Antes de la venta', en: 'Before the sale' },
               items: [
                  { es: 'Captación', en: 'Lead capture' },
                  { es: 'Cualificación', en: 'Qualification' },
                  { es: 'Seguimiento de oportunidades', en: 'Opportunity follow-up' },
                  { es: 'Contenido', en: 'Content' },
               ],
            },
            {
               key: 'post',
               title: { es: 'Después de la venta', en: 'After the sale' },
               items: [
                  { es: 'Reactivación de clientes', en: 'Client reactivation' },
                  { es: 'Campañas', en: 'Campaigns' },
                  { es: 'Reseñas', en: 'Reviews' },
                  { es: 'Recompra', en: 'Repeat purchase' },
                  { es: 'Análisis de datos', en: 'Data analysis' },
               ],
            },
         ],
         note: {
            es: 'Cada ampliación es una etapa más del mismo sistema o un Playbook adicional.',
            en: 'Every extension is one more stage of the same system, or an extra Playbook.',
         },
      },
      // ---------------------------------------------------------------- 6
      {
         key: 'prove',
         icon: 'chart',
         name: { es: 'Demuestra el retorno', en: 'Prove the return' },
         headline: {
            es: 'No solo vendes automatización. Demuestras su impacto y justificas la continuidad del servicio.',
            en: 'You do not just sell automation. You prove its impact and justify keeping the service.',
         },
         metrics: [
            { key: 'activity', label: { es: 'Actividad ejecutada', en: 'Activity run' }, value: '1.284', isEstimate: true },
            { key: 'leads', label: { es: 'Leads atendidos', en: 'Leads handled' }, value: '312', isEstimate: true },
            { key: 'appointments', label: { es: 'Citas generadas', en: 'Appointments booked' }, value: '148', isEstimate: true },
            { key: 'conversions', label: { es: 'Conversiones', en: 'Conversions' }, value: '37', isEstimate: true },
            { key: 'time', label: { es: 'Tiempo ahorrado', en: 'Time saved' }, value: '64 h', isEstimate: true },
            { key: 'revenue', label: { es: 'Facturación atribuida', en: 'Attributed revenue' }, value: '18.400 €', isEstimate: true },
            { key: 'cost', label: { es: 'Coste', en: 'Cost' }, value: '1.950 €', isEstimate: true },
            { key: 'roi', label: { es: 'ROI', en: 'ROI' }, value: '9,4x', highlight: true, isEstimate: true },
         ],
         cta: {
            label: { es: 'Explorar Playbooks', en: 'Explore Playbooks' },
            to: '/playbooks',
         },
      },
   ],
};

export default playbookStory;
